import createCache from '@emotion/cache';
import { blueGrey, grey } from '@mui/material/colors';
import { createTheme } from '@mui/material/styles';

// Custom Stylis plugin to add !important to all declarations
const importantPlugin = (element) => {
  if (element.type === 'decl') {
    if (
      !element.value.includes('!important') &&
      !element.props.includes('transition') &&
      !element.props.includes('transform')
    ) {
      element.value = element.value.replace(/;/g, ' !important;');
    }
  }
};

const scaleBase = 1.125;
const getFontScale = (n) => Math.pow(scaleBase, n);

const theme = createTheme({
  spacing: (factor) => `${0.5 * factor}rem`,
  colorSchemes: {
    light: {
      palette: {
        // contrastThreshold: 3,  // default: 3
        tonalOffset: 0.2, // for calculating light and dark colors. Higher means more contrast
        primary: {
          main: '#00B4AA',
          50: '#F2FBFA',
          300: '#60D0CA',
          dark: '#009B92',
          contrastText: '#FFFFFF',
        },
        secondary: {
          main: '#05354E',
          50: '#f8fafb',
          500: '#375D71',
          contrastText: '#FFFFFF',
        },
        error: {
          main: '#DC2626',
          contrastText: '#FFFFFF',
        },
        info: {
          main: '#6D7479',
          200: '#C0C3C5',
          300: '#A4A8AB',
          700: '#414548',
          contrastText: '#FFFFFF',
        },
        success: {
          main: '#22C55E',
          contrastText: '#FFFFFF',
        },
        warning: {
          light: '#FFF7CD',
          main: '#F5CD3D',
          contrastText: '#FFFFFF',
        },
        background: {
          default: '#E6ECF0',
          paper: '#FFFFFF',
        },
        green: {
          100: '#D0F1C4',
          500: '#3CC50C',
          800: '#184E04',
        },
        yellow: {
          100: '#FFF3C1',
          800: '#665300',
        },
        blueGrey,
      }
    },
    // dark: {},  // customise dark mode palette here
  },
  shadows: [
    'none',
    '1px 5px 2px 0px rgba(0, 0, 0, 0.01), 1px 3px 2px 0px rgba(0, 0, 0, 0.03), 0px 1px 1px 0px rgba(0, 0, 0, 0.04), 0px 0px 1px 0px rgba(0, 0, 0, 0.05)',
    '3px 12px 5px 0px rgba(0, 0, 0, 0.01), 2px 7px 4px 0px rgba(0, 0, 0, 0.03), 1px 3px 3px 0px rgba(0, 0, 0, 0.04), 0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
    '6px 21px 9px 0px rgba(0, 0, 0, 0.01), 3px 12px 7px 0px rgba(0, 0, 0, 0.03), 1px 5px 6px 0px rgba(0, 0, 0, 0.05), 0px 1px 3px 0px rgba(0, 0, 0, 0.06)',
    '8px 28px 12px 0px rgba(0, 0, 0, 0.01), 4px 16px 10px 0px rgba(0, 0, 0, 0.04), 2px 7px 8px 0px rgba(0, 0, 0, 0.06), 0px 2px 4px 0px rgba(0, 0, 0, 0.07)',
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
    // Some components use elevation 24 so we need to provide enough shadows
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
    '10px 39px 16px 0px rgba(0, 0, 0, 0.01), 6px 22px 14px 0px rgba(0, 0, 0, 0.05), 3px 10px 10px 0px rgba(0, 0, 0, 0.08), 1px 2px 5px 0px rgba(0, 0, 0, 0.09)',
  ],
  insets: [
    'none',
    'inset 1px 1px 2px 0px rgba(0, 0, 0, 0.05), inset 1px 1px 1px 0px rgba(0, 0, 0, 0.09)',
    'inset 2px 3px 2px 0px rgba(0, 0, 0, 0.05), inset 1px 1px 2px 0px rgba(0, 0, 0, 0.09)',
  ],
  typography: {
    h1: {
      fontSize: `${getFontScale(6)}em`,
    },
    h2: {
      fontSize: `${getFontScale(5)}em`,
    },
    h3: {
      fontSize: `${getFontScale(4)}em`,
    },
    h4: {
      fontSize: `${getFontScale(3)}em`,
    },
    h5: {
      fontSize: `${getFontScale(2)}em`,
    },
    h6: {
      fontSize: `${getFontScale(1)}em`,
    },
    // body1: {},
    body2: {
      fontSize: `${getFontScale(-0.5)}em`,
    },
    subtitle1: {
      fontSize: `${getFontScale(-1)}em`
    },
    subtitle2: {
      fontSize: `${getFontScale(-1.5)}em`
    },
    button: {
      fontSize: `${getFontScale(-0.5)}em`,
    },
    caption: {
      fontSize: `${getFontScale(-2)}em`,
    },
    tiny: {
      fontSize: `${getFontScale(-3)}em`,
    },
    // overline: {},
    fontSize: 14, // default 14px
    fontFamily: [
      'Avenir',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'Capitalize',
          variants: [
            {
              props: { size: 'small' },
              style: ({ theme }) => ({
                fontSize: theme.typography.subtitle1.fontSize,
                fontWeight: 400,
                gap: theme.spacing(0.5),
              }),
            },
            {
              props: { size: 'medium' },
              style: ({ theme }) => ({
                fontSize: theme.typography.body1.fontSize,
                fontWeight: 400,
                gap: theme.spacing(1),
              })
            },
            {
              props: { size: 'large' },
              style: ({ theme }) => ({
                fontSize: theme.typography.h6.fontSize,
                gap: theme.spacing(2),
              })
            },
            {
              props: { variant: 'menu' },
              style: ({ theme }) => ({
                width: '100%',
                color: grey[600],
                justifyContent: 'flex-start',
                fontSize: theme.typography.body1.fontSize,
                fontWeight: 400,
                padding: theme.spacing(1, 2),
                gap: theme.spacing(1),
                textTransform: 'none',
              })
            },
            {
              props: { variant: 'menu-active' },
              style: ({ theme }) => ({
                width: '100%',
                justifyContent: 'flex-start',
                fontSize: theme.typography.body1.fontSize,
                fontWeight: 600,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                padding: theme.spacing(1, 2),
                gap: theme.spacing(1),
                textTransform: 'none',
              })
            }
          ]
        },
        startIcon: ({ theme }) => ({
          fontSize: theme.typography.subtitle1.fontSize,
          marginRight: 0,
          marginLeft: 0,
        }),
        endIcon: ({ theme }) => ({
          fontSize: theme.typography.subtitle1.fontSize,
          marginRight: 0,
          marginLeft: 0,
        }),
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { size: 'small' },
              style: ({ theme }) => ({
                fontSize: theme.typography.tiny.fontSize,
                fontWeight: 400,
                height: theme.typography.caption.fontSize,
                padding: theme.spacing(0, 0.5),
              }),
            },
            {
              props: { size: 'medium' },
              style: ({ theme }) => ({
                fontSize: theme.typography.caption.fontSize,
                fontWeight: 400,
                height: theme.typography.body1.fontSize,
                gap: theme.spacing(0.5, 1),
              })
            },
            {
              props: { color: 'info' },
              style: ({ theme }) => ({
                backgroundColor: theme.palette.info.light,
                color: 'green',
              })
            },
          ]
        },
      }
    },
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          borderRadius: theme.spacing(1),
          borderColor: grey[300],
          borderWidth: 1,
          boxShadow: theme.insets[1],
          ['&.Mui-error']: {
            borderColor: theme.palette.error.main,
          },
          ['&:hover']: {
            backgroundColor: grey[100],
          },
          ['&.Mui-focused']: {
            backgroundColor: grey[100],
            borderColor: theme.palette.primary.main,
          },
          ['& input[readonly]']: {
            boxShadow: 'none',
            backgroundColor: grey[200],
            border: 0,
            color: grey[500],
          }
        }),
      },
    },
    MuiInputBase: {
      defaultProps: {
        fullWidth: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          ['&.Mui-error']: {
            borderColor: theme.palette.error.main,
          },
          ['& input[readonly]']: {
            boxShadow: 'none',
            backgroundColor: grey[200],
            border: 0,
            color: grey[500],
          }
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { size: 'small' },
              style: ({ theme }) => ({
                fontSize: theme.typography.subtitle1.fontSize,
                fontWeight: 400,
              }),
            },
            {
              props: { size: 'medium' },
              style: ({ theme }) => ({
                fontSize: theme.typography.body1.fontSize,
                fontWeight: 400,
              })
            },
            {
              props: { size: 'large' },
              style: ({ theme }) => ({
                fontSize: theme.typography.h6.fontSize,
              })
            },
          ]
        },
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.spacing(2),
          overflow: 'hidden',
          borderColor: grey[300],
          borderWidth: 1,
          variants: [
            {
              props: { variant: 'outlined' },
              style: () => ({
                backgroundColor: 'transparent',
              }),
            }
          ],
        }),
      }
    },
    MuiPopover: {
      defaultProps: {
        elevation: 3,
        slotProps: {
          paper: {
            sx: (theme) => ({
              borderRadius: theme.spacing(1),
            })
          }
        }
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'filled',
        fullWidth: true,
        size: 'small',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          borderRadius: theme.spacing(1),
          borderColor: grey[300],
          borderWidth: 1,
          boxShadow: theme.insets[2],
        }),
      }
    },
    MuiTable: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          paddingTop: theme.spacing(2),
          paddingBottom: theme.spacing(2),
          paddingLeft: theme.spacing(3),
          paddingRight: theme.spacing(3),
          border: 0,
          color: theme.palette.text.primary,
          borderBottomColor: theme.palette.grey[200],
          variants: [
            {
              props: { variant: 'head' },
              style: ({ theme }) => ({
                color: theme.palette.grey[500],
                fontWeight: 600,
                fontSize: theme.typography.subtitle1.fontSize,
                lineHeight: 1.2,
              })
            }
          ]
        }),
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.default,
        }),
      }
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: () => ({
          lineHeight: 'inherit',
        }),
      }
    },
    MuiTextField: {
      defaultProps: {
        variant: 'filled',
        fullWidth: true,
        size: 'small',
      },
    },
    MuiTimelineItem: {
      styleOverrides: {
        root: () => ({
          minHeight: 'auto',
        }),
      },
    },
  },
});

// Create emotion cache that adds !important to all styles
export const muiCache = createCache({
  key: 'mui',
  prepend: true,
  stylisPlugins: [importantPlugin]
});

export default theme;
